import type { FC } from 'react';
import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { get } from 'lodash';
import styled from 'styled-components';

import moonIcon from '@lt/components/icons-react/userflow/Moon';
import starIcon from '@lt/components/icons-react/userflow/Star';

import { useTranslation } from '@lt/localization';
import type { ISidebarHotel } from '../../../../types/hotelTypes';

import {
  getHotelLocation,
  getHotelStars,
  getHotelTitle,
  getHotelImage,
  getHotelRoulette,
} from '../../../../store/selectors/hotel';
import {
  getEndDate,
  getTouristsText,
  getMeal,
  getNightsInHotel,
  getStartDate,
  getNightsCount,
} from '../../../../store/selectors/package';
import { formatDateToDayShort } from '../../../../../../utils/dateUtils';
import { goToHotelSectionAction } from '../../../../store/actions/view';
import AboutRoomButton from '../../../../components/AboutRoom';
import OnlyWithRoomInfo from '../../../../components/AboutRoom/OnlyWithRoomInfo';
import Nbsp from '../../../../../../components/Nbsp';
import { getAssets } from '../../../../store/selectors/assets';

const baseCls = 'checkout-sidebar-hotel';

const MealSeparator = OnlyWithRoomInfo(() => ' · ');

const SidebarHotel: FC<ISidebarHotel> = ({
  location,
  name,
  stars,
  className = '',
  touristsText,
  startDate,
  endDate,
  image,
  nights,
  originalNights,
  meal,
  goToHotel,
}) => {
  const { t } = useTranslation();
  const wrapperCls = classNames(baseCls, { [className]: className });
  const startDateShort = formatDateToDayShort(startDate);
  const endDateShort = formatDateToDayShort(endDate);

  const nightsDiff = originalNights - nights;

  const formattedNights = (
    <StyledNightsText>
      {nights}
      <StyledMoonIcon />{' '}
      {nightsDiff ? (
        <>
          (+{nightsDiff}
          <StyledMoonIcon /> {t('checkout.sidebar.flight')})
        </>
      ) : null}
    </StyledNightsText>
  );

  return (
    <div className={wrapperCls}>
      <div className="flex">
        <div className={`${baseCls}__info`}>
          <button
            className="checkout-sidebar-button"
            type="button"
            onClick={goToHotel}
          >
            <StyledHeaderWrapper>
              <div className="like-h4">
                {`${name},`}
                <Nbsp />
                <span className="nowrap">
                  {stars}
                  <StyledStarIcon />
                </span>
              </div>
              <img
                className={`${baseCls}__image`}
                src={image}
                width={40}
                height={40}
                alt={name}
              />
            </StyledHeaderWrapper>
          </button>

          <div className={`${baseCls}__dates`}>
            <span className="nowrap">{startDateShort}</span>
            <span className={`${baseCls}__nights`}>{formattedNights}</span>
            <span className="nowrap">{`${endDateShort},`}</span>
          </div>
          <div className={`${baseCls}__tourists`}>{touristsText}</div>
        </div>
      </div>
      <div className={`${baseCls}__info`}>
        <div className={`${baseCls}__text_small-gray`}>{location}</div>
        <div className={`${baseCls}__text_small-gray`}>
          <AboutRoomButton />
          <MealSeparator />
          {meal}
        </div>
      </div>
    </div>
  );
};

const StyledHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledNightsText = styled.div`
  color: #4d4d4d;
`;

const StyledMoonIcon = styled(moonIcon)`
  transform: translate(0px, 0.5px);
  width: 7px;
  height: 7px;
`;

const StyledStarIcon = styled(starIcon)`
  fill: #ffd600;
  margin-left: 3px;
  width: 12px;
  height: 12px;
`;

const mapStateToProps = (state) => {
  const isRoulette = getHotelRoulette(state);
  const assets = getAssets(state);
  return {
    location: getHotelLocation(state),
    name: getHotelTitle(state),
    stars: getHotelStars(state),
    startDate: getStartDate(state),
    endDate: getEndDate(state),
    touristsText: getTouristsText(state),
    image: isRoulette
      ? assets['packages/fortuna-room@2x.jpg']
      : get(getHotelImage(state), 'x55x56.url'),
    nights: getNightsInHotel(state),
    originalNights: getNightsCount(state),
    meal: getMeal(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  goToHotel: () => dispatch(goToHotelSectionAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SidebarHotel);
